<!-- Overrides @basecom-gmbh/pond/components/Cms/Element/CmsElementproductDescriptionReviews.vue -->
<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { CmsElementProductDescriptionReviews } from '@shopware-pwa/composables-next';
import { getTranslatedProperty } from '@shopware-pwa/helpers-next';
import type { Schemas } from '@shopware/api-client/api-types';

const props = defineProps<{
    element: CmsElementProductDescriptionReviews;
}>();
const { t } = useI18n();
const { product } = useProduct(props.element.data.product);
const propertyGroups = ref<Schemas['PropertyGroup'][]>();
propertyGroups.value = product.value.properties
    .map((obj: Schemas['PropertyGroupOption']) => obj.group)
    .filter((obj: Schemas['PropertyGroup'], index: number, arr: Schemas['PropertyGroup'][]) => {
        return arr.findIndex((item: Schemas['PropertyGroup']) => item.id === obj.id) === index;
    });

const { getCategoryLink, getCountrySpecificVendor } = useVendor();
const countrySpecificVendor = await getCountrySpecificVendor(product.value?.extensions?.vendors);
const manufacturerLink = await getCategoryLink(product.value.manufacturer?.id);
const vendorLink = await getCategoryLink(countrySpecificVendor?.id);

const getPropertiesOfGroup = (groupId: string): Schemas['PropertyGroupOption'][] => {
    return product.value.properties.filter((property: Schemas['PropertyGroupOption']) => property.group.id === groupId);
};

const getPropertyNamesOfGroup = (groupId: string) => {
    const properties = getPropertiesOfGroup(groupId);

    return properties.map(property => getTranslatedProperty(property, 'name'));
};

// IDS-84 - extract "Chracteristics" from property groups
const propertyGroupsData = computed(() => {
    const allGroups = product.value.properties.map((obj: Schemas['PropertyGroupOption']) => obj.group);

    const characteristics =
        allGroups.find((group: Schemas['PropertyGroup']) => group.name === 'Characteristics') || null;

    const propertyGroups = allGroups.filter(
        (obj: Schemas['PropertyGroup'], index: number, arr: Schemas['PropertyGroup'][]) => {
            return (
                arr.findIndex((item: Schemas['PropertyGroup']) => item.id === obj.id) === index &&
                obj.name !== 'Characteristics'
            );
        },
    );

    return {
        propertyGroups,
        characteristics,
    };
});

const productSafetyNotices = computed(() => {
    const listOfNoticesString = product.value?.customFields?.product_safety_notices;
    if (listOfNoticesString) {
        try {
            const listOfNotices: string[] = listOfNoticesString?.split(',') ?? [];
            if(!Array.isArray(listOfNotices)) {
                return [];
            }
            return listOfNotices.map((noticeString: string) => {
                return {
                    title: t(`product.safetyNotices.${noticeString}.title`),
                    text:  t(`product.safetyNotices.${noticeString}.text`),
                };
            });
        } catch (e) {
            console.error('Error parsing product safety notices:', e);
            return [];
        }
    } else {
        return [];
    }
});

const openedSafetyNotices = ref([]);


const openOrClose = (idx: number) => {
    if(openedSafetyNotices.value.indexOf(idx) === -1) {
        openedSafetyNotices.value.push(idx);
    } else {
        openedSafetyNotices.value = openedSafetyNotices.value.filter(i => i !== idx);
    }
}

// CUSTOM IDS-223: Display additional custom fields for technical info
const additionalProductProperties: Record<string, string> = {
    cleaningMaterial: t('product.cleaningMaterial'),
    exposureVolume: t('product.exposureVolume'),
    installationSpace: t('product.installationSpace'),
    nativePixels: t('product.nativePixels'),
    processingTime: t('product.processingTime'),
    volume: t('product.volume'),
    washVolume: t('product.washVolume'),
};

const additionalProductPropertiesArray = computed(() => {
    return Object.entries(product.value.translated.customFields ?? {})
        .filter(([key, _]) => key in additionalProductProperties)
        .map(([key, value]) => ({
            name: additionalProductProperties[key],
            value: value ?? product.value.customeFields[key],
        }))
        .filter(({ value }) => value);
});
// IDS-84 - get product downloads if any
const downloads = useState('productDownloads');
</script>

<template>
    <div class="grid grid-cols-8 gap-8">
        
<FormKitLazyProvider config-file="true">
<div
            v-if="getTranslatedProperty(product, 'description').length"
            class="col-span-8"
        >
            <div class="flex flex-col gap-8 md:flex-row">
                <!-- v-html is necessary because we need to render html -->
                <!-- eslint-disable vue/no-v-html -->
                <div
                    class="wysiwyg flex-grow"
                    v-html="getTranslatedProperty(product, 'description')"
                />
            </div>
        </div>

        <div
            v-if="productSafetyNotices?.length > 0"
            class="col-start-1 col-end-9 row-span-4 md:col-start-5 md:col-end-9 flex flex-col gap-1">
                <h3 class="font-black">{{t('product.safetyNotices.title')}}</h3>
                <div v-for="(notice, idx) in productSafetyNotices" class="flex flex-col gap-1">
                    <div class="flex gap-1 cursor-pointer" @click="openOrClose(idx)">
                        <FormKitIcon
                            icon="triangle"
                            class="w-4 h-4 my-auto transition-transform"
                            :class="openedSafetyNotices.indexOf(idx) > -1 ? '' : '-rotate-90'"
                        />
                        <div class="text-[15px] font-bold">
                            {{notice.title}}
                        </div>
                    </div>
                    <div v-if="openedSafetyNotices.indexOf(idx)> -1" class="ml-5">
                        {{notice.text}}
                    </div>
                </div>
        </div>
        <div
            v-if="
                propertyGroupsData.propertyGroups.length ||
                additionalProductPropertiesArray.length ||
                product.translated?.customFields?.product_construction_area_width ||
                product.translated?.customFields?.product_construction_area_depth ||
                product.translated?.customFields?.product_maximum_workpiece_height
            "
            class="col-start-1 col-end-9 row-span-4 md:col-start-5 md:col-end-9"
        >
            <h3 class="font-black">{{ t('product.technicalInformation') }}</h3>
            <table class="table-auto6 w-full">
                <tbody>
                <tr
                    v-if="product.customFields?.manufacturerProductNumber"
                    class="border-neutral-300 [&:not(:last-child)]:border-b-2"
                >
                    <td class="w-1/3 border-r-2 border-neutral-300 py-4 pr-4">
                        {{ t('product.manufacturerProductNumber') }}
                    </td>
                    <td class="w-2/3 py-4 pl-6 pr-4">
                        {{ product.customFields.manufacturerProductNumber }}
                    </td>
                </tr>
                    <tr
                        v-for="group in propertyGroupsData.propertyGroups"
                        :key="`group-${group.id}`"
                        class="border-neutral-300 [&:not(:last-child)]:border-b-2"
                    >
                        <td class="w-1/3 border-r-2 border-neutral-300 py-4 pr-4">
                            {{ getTranslatedProperty(group, 'name') }}
                        </td>
                        <td class="w-2/3 py-4 pl-6 pr-4">
                            {{ getPropertyNamesOfGroup(group.id).join(', ') }}
                        </td>
                    </tr>

                    <!-- IDS-190: add custom fields -->
                    <tr
                        v-if="product.translated?.customFields?.product_construction_area_width"
                        class="border-neutral-300 [&:not(:last-child)]:border-b-2"
                    >
                        <td class="w-1/3 border-r-2 border-neutral-300 py-4 pr-4">
                            {{ t('product.constructionAreaWidth') }}
                        </td>
                        <td class="w-2/3 py-4 pl-6 pr-4">
                            {{ product.translated.customFields.product_construction_area_width }}
                        </td>
                    </tr>
                    <tr
                        v-if="product.translated?.customFields?.product_construction_area_depth"
                        class="border-neutral-300 [&:not(:last-child)]:border-b-2"
                    >
                        <td class="w-1/3 border-r-2 border-neutral-300 py-4 pr-4">
                            {{ t('product.constructionAreaDepth') }}
                        </td>
                        <td class="w-2/3 py-4 pl-6 pr-4">
                            {{ product.translated.customFields.product_construction_area_depth }}
                        </td>
                    </tr>
                    <tr
                        v-if="product.translated?.customFields?.product_maximum_workpiece_height"
                        class="border-neutral-300 [&:not(:last-child)]:border-b-2"
                    >
                        <td class="w-1/3 border-r-2 border-neutral-300 py-4 pr-4">
                            {{ t('product.workpieceHeight') }}
                        </td>
                        <td class="w-2/3 py-4 pl-6 pr-4">
                            {{ product.translated.customFields.product_maximum_workpiece_height }}
                        </td>
                    </tr>
                    <!--                    CUSTOM IDS-223: Display custom fields for technical info-->
                    <tr
                        v-for="property in additionalProductPropertiesArray"
                        :key="property.name"
                        class="border-neutral-300 [&:not(:last-child)]:border-b-2"
                    >
                        <td class="w-1/3 border-r-2 border-neutral-300 py-4 pr-4">
                            {{ property.name }}
                        </td>
                        <td class="w-2/3 py-4 pl-6 pr-4">
                            {{ property.value }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div
            v-if="getPropertiesOfGroup(propertyGroupsData.characteristics?.id).length"
            class="col-start-1 col-end-9 row-end-auto md:col-start-5"
        >
            <h3 class="font-black">{{ t('product.characteristics') }}</h3>

            <div class="mt-6 flex flex-col gap-2 md:flex-row">
                <div
                    v-for="characteristics of getPropertiesOfGroup(propertyGroupsData.characteristics?.id)"
                    :key="characteristics.id"
                    class="has-tooltip flex cursor-default place-items-center gap-4 md:cursor-pointer"
                >
                    <img
                        :src="characteristics.media?.url"
                        alt="characteristic"
                        class="size-10"
                    />
                    <span class="tooltip !hidden md:!inline-block">
                        {{ getTranslatedProperty(characteristics, 'name') }}
                    </span>
                    <span class="md:hidden">
                        {{ getTranslatedProperty(characteristics, 'name') }}
                    </span>
                </div>
            </div>
        </div>

        <div
            v-if="downloads?.length"
            class="col-start-1 col-end-9 md:col-start-5"
        >
            <h3 class="font-black">{{ t('product.downloads') }}</h3>

            <div class="mt-6 flex flex-col gap-2">
                <LocaleLink
                    v-for="download of downloads"
                    :key="download.id"
                    :to="download.media.url"
                    target="_blank"
                    class="flex gap-2 underline underline-offset-4"
                >
                    <FormKitIcon
                        icon="file-pdf"
                        class="block w-3"
                    />
                    <span>
                        {{ download?.translated?.name || truncateFileName(download?.media?.fileName) || 'Information for use (IFU)' }} ({{`${download?.media?.fileExtension?.toUpperCase()}, ${formatBytes(download?.media?.fileSize)}`}})
                    </span>
                </LocaleLink>
            </div>
        </div>
        <div
            class="col-start-1 col-end-9 md:col-start-5 grid grid-cols-2">
            <div v-if="product.customFields?.['3rd_party_manufacturer']" class="col-span-1">
                <h3 class="font-black py-1">{{ t('product.manufacturer') }}</h3>
                <div v-html="product.customFields?.['3rd_party_manufacturer']" />
                <template v-if="countrySpecificVendor">
                    <h3 class="font-black pt-12 pb-1">{{ t('product.soldAndShippedBy') }}</h3>
                    <div v-html="countrySpecificVendor?.address"></div>
                    <LocaleLink class="font-bold underline" :to="vendorLink">{{ t('product.infoTerms') }}</LocaleLink>
                </template>
            </div>
            <div v-else class="col-span-1">
                <h3 class="font-black py-1">{{ t('product.manufacturer') }}</h3>
                <div v-html="product?.manufacturer?.translated?.customFields?.address" />
                <LocaleLink class="font-bold underline" :to="manufacturerLink">{{ t('product.moreFromManufacturer', {manufacturerName: product?.manufacturer?.translated?.name }) }}</LocaleLink>
                <template v-if="countrySpecificVendor">
                    <h3 class="font-black pt-12 pb-1">{{ t('product.soldAndShippedBy') }}</h3>
                    <div v-html="countrySpecificVendor?.address"></div>
                    <LocaleLink class="font-bold underline" :to="vendorLink">{{ t('product.infoTerms') }}</LocaleLink>
                </template>
            </div>
            <div v-if="!product.customFields?.['3rd_party_manufacturer']" class="hidden  col-span-1 mt-10 md:flex flex-row-reverse">
                <ProductManufacturer  :manufacturer="product.manufacturer" />
            </div>

        </div>
</FormKitLazyProvider>

    </div>
</template>

<style scoped>
.wysiwyg {
    :deep(ul) {
        @apply list-inside list-disc;
    }

    :deep(b) {
        @apply my-8 text-xl font-bold;
    }

    :deep(h4) {
        @apply text-base font-bold mt-4;
    }

    :deep(p) {
        @apply mb-4;
    }
}
</style>
